import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, CircularProgress, Snackbar, Alert
} from '@mui/material';
import axios from 'axios';

export default function TierPricingContent() {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tierPricing, setTierPricing] = useState({
    free: { monthlyPrice: 0, yearlyPrice: 0 },
    standard: { monthlyPrice: 29.99, yearlyPrice: 299.99 },
    professional: { monthlyPrice: 29.99, yearlyPrice: 299.99 },
    enterprise: { monthlyPrice: 79.99, yearlyPrice: 799.99 }
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get('/api/config/bootstrap');
        if (response.data.success && response.data.config.tierPricing) {
          setTierPricing(response.data.config.tierPricing);
        }
      } catch (error) {
        console.error('Error fetching configuration:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const updateTierPrice = (tier, field, value) => {
    setTierPricing(prev => ({
      ...prev,
      [tier]: { ...prev[tier], [field]: parseFloat(value) || 0 }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axios.post('/api/config/tier-pricing', { tierPricing, updatedBy: 'admin' });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Tier pricing updated successfully', severity: 'success' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save tier pricing', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
		<Box sx={{ width: '100%', minWidth: 0 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure pricing for each subscription tier
      </Typography>

			<Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
        {['free', 'standard', 'professional', 'enterprise'].map((tier) => (
          <Grid item xs={12} key={tier}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>{tier} Tier</Typography>
							<Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Monthly Price ($)"
                    type="number"
                    value={tierPricing[tier]?.monthlyPrice || 0}
                    onChange={(e) => updateTierPrice(tier, 'monthlyPrice', e.target.value)}
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Yearly Price ($)"
                    type="number"
                    value={tierPricing[tier]?.yearlyPrice || 0}
                    onChange={(e) => updateTierPrice(tier, 'yearlyPrice', e.target.value)}
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={24} /> : 'Save Tier Pricing'}
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

